import Environment from '@omni/kit/Environment';
import KitIcon from '@omni/kit/components/KitIcon';
import { IMediaPlaybackStateInfo } from '@omni/kit/components/KitPlayer/playbackTypes';
import KitText from '@omni/kit/components/KitText';
import KitTouchable from '@omni/kit/components/KitTouchable';
import { useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import AnalyticsService from '@omni/kit/services/AnalyticsService';
import { IBroadcast } from '@omni/kit/services/BroadcastService';
import MediaItemService, {
  IMediaItem,
} from '@omni/kit/services/MediaItemService';
import PlayerPromptService, {
  IPlayerPrompt,
} from '@omni/kit/services/PlayerPromptService';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { getMediaPlaybackStateInfo } from '@omni/kit/utilities/NativeHelpers';
import { usePrevious } from '@omni/kit/utilities/utilities';
import moment from 'moment';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import {
  EmitterSubscription,
  Falsy,
  NativeEventEmitter,
  NativeModules,
  Platform,
  View,
  ViewStyle,
} from 'react-native';

import { openLink } from '../../utilities/links';
import KitSafeAreaView from '../KitSafeAreaView/KitSafeAreaView';
import GiveAmounts from './GiveAmounts';
import { PromptEvent } from './Types';
import usePlayerPromptSize from './usePlayerPromptSize';

const debug = (...s: any) => undefined; //require('debug')('tca:kit:components:PlayerPrompt');

const { ReactPlatformBridge } = NativeModules;

const POLL_POSITION_INTERVAL = 1 * 1000; // 1 second
const POLL_PROMPT_INTERVAL = 10 * 1000; // 10 seconds
const BUFFER_GRACE_RESET_TIMEOUT = 30 * 1000; // 30 seconds

let bufferGrace = false;
let pollPlayerPromptId: ReturnType<typeof setTimeout>;
let pollPositionId: ReturnType<typeof setTimeout> | null;
let pollBroadcastId: ReturnType<typeof setTimeout>;
let bufferGraceTimeoutId: ReturnType<typeof setTimeout>;

interface IMediaPlayerEvents {
  durationMsec?: number;
  isLiveStream?: boolean;
  isNativeBuffering?: boolean;
  isPlaying?: boolean;
  isSeekable?: boolean;
  isSeeking?: boolean;
  mediaItemShortCode?: string;
  playId?: string;
  positionMsec?: number;
}

export default (
  props: {
    appKey?: string;
    guestToken?: string;
    useNativeMediaPlayer?: boolean;
    styles?: ViewStyle | Falsy;
  } & IMediaPlayerEvents
): ReactElement | null => {
  const { windowWidth } = useSizeClass();

  const { maxWidth, contentMargin } = usePlayerPromptSize();
  let pollPlayerPromptsErrorTally = 0;

  /**  -------------------- COMPONENT STATES -------------------- */

  const [broadcast, setBroadcast] = useState<IBroadcast | undefined>(undefined);
  const [broadcastEstimatedStartTime] = useState<string>(moment.utc().format());
  const [dismissedPrompt, setDismissedPrompt] = useState<boolean>(false);
  const [durationMsec, setDurationMsec] = useState<number | undefined>(
    undefined
  );
  const [givingCardVisible, setGivingCardVisible] = useState(false);
  const [givingUrl, setGivingUrl] = useState<string>('');
  const [isBuffering, setIsBuffering] = useState<boolean | undefined>(
    undefined
  );
  const [isLiveStream, setIsLiveStream] = useState<boolean | undefined>(
    undefined
  );
  const [isNativeBuffering, setIsNativeBuffering] = useState<
    boolean | undefined
  >(undefined);
  const [isPlaying, setIsPlaying] = useState<boolean | undefined>(undefined);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [isSeekable, setIsSeekable] = useState<boolean | undefined>(undefined);
  const [isSeeking, setIsSeeking] = useState<boolean | undefined>(undefined);
  const [mediaItemShortCode, setMediaItemShortCode] = useState<
    string | undefined
  >(undefined);
  const [playerPrompt, setPlayerPrompt] = useState<IPlayerPrompt | undefined>(
    undefined
  );
  const [playId, setPlayId] = useState<string | undefined>(undefined);
  const [positionMsec, setPositionMsec] = useState<number | undefined>(
    undefined
  );
  const [promptEndTime, setPromptEndTime] = useState<number | null>(null);
  const [promptStartTime, setPromptStartTime] = useState<number | null>(null);

  const prevIsNativeBuffering = usePrevious(isNativeBuffering);

  /** ------------------------- METHODS ------------------------- */

  const updatePosition = () => {
    getMediaPlaybackStateInfo()
      .then((info: IMediaPlaybackStateInfo) => {
        setPositionMsec(info.positionMsec);
      })
      .catch((e) => {
        debug('Failed to update media position', e);
      });
  };

  const updatePlaybackStates = useCallback((event: IMediaPlaybackStateInfo) => {
    debug('updatePlaybackStates', event);

    setDurationMsec(event.durationMsec);
    setIsLiveStream(event.isLiveStream);
    setIsNativeBuffering(event.isBuffering);
    setIsPlaying(event.isPlaying);
    setIsSeekable(event.isSeekable);
    setIsSeeking(event.isSeeking);
    setMediaItemShortCode(event.mediaItemShortCode);
    setPlayId(event.playId);
    setPositionMsec(event.positionMsec);
  }, []);

  /** Javascript bridge media event listener */
  const _registerEmitters = useCallback((): EmitterSubscription[] | null => {
    if (ReactPlatformBridge != null) {
      const ReactPlatformBridgeEventEmitter = new NativeEventEmitter(
        ReactPlatformBridge
      );
      const mediaPlaybackStateChangedSubscription =
        ReactPlatformBridgeEventEmitter.addListener(
          'MediaPlaybackStateChanged',
          updatePlaybackStates
        );

      return [mediaPlaybackStateChangedSubscription];
    } else {
      return null;
    }
  }, [updatePlaybackStates]);

  /** Poll player prompt */
  const pollPlayerPrompts = async (props: {
    mediaItem: IMediaItem;
    appKey: string;
  }) => {
    /** Ensure any previous timeouts are cleared before setting a new one */
    clearTimeout(pollPlayerPromptId);
    debug('polling for player prompt');
    const { mediaItem } = props;
    let wait = POLL_PROMPT_INTERVAL;

    try {
      const response = await PlayerPromptService.getAll({
        appKey: props.appKey,
        filters: [
          ['media-item.id', mediaItem.id],
          ['app_key', props.appKey],
        ],
      });

      const prompts = response.body?._embedded['player-prompts'];

      /** Reset poll error tally if polling succeeds */
      pollPlayerPromptsErrorTally = 0;
      const prompt = prompts?.[0];

      if (prompt) {
        debug('Received a player prompt. End polling.', prompt);
        setPlayerPrompt(prompt);

        return;
      }
    } catch (e) {
      console.warn('poll player prompts failed', {
        e,
        pollPlayerPromptsErrorTally,
      });
      /** Stops polling if token is expired */
      // @ts-ignore
      if (e.status === 401) return console.warn('Stop polling player prompt');

      /**
       * Pause polling for 5 minutes then resume
       * if the error code is 429 or polling failed 5 times in a row
       */
      // @ts-ignore
      if (e.status === 429 || pollPlayerPromptsErrorTally >= 5) {
        console.warn(
          'Pause polling for 5 minutes then resume polling player prompt'
        );
        pollPlayerPromptsErrorTally = 0;
        wait = 5 * 60 * 1000; /** 5 minutes */
      } else {
        pollPlayerPromptsErrorTally++;
      }
    }

    pollPlayerPromptId = setTimeout(() => pollPlayerPrompts(props), wait);
  };

  /** Fetch resoures */
  const initFetches = async () => {
    debug('initFetches');
    try {
      const response = await MediaItemService.getAll({
        filters: [
          ['app_key', props.appKey || ''],
          ['short_code', mediaItemShortCode || ''],
        ],
        includes: ['broadcast'],
        appKey: props.appKey,
      });

      const mediaItems = response.body?._embedded['media-items'];

      const mediaItem = mediaItems?.[0];

      if (!mediaItem) throw new Error('Media item cannot be found');

      const broadcast = mediaItem._embedded?.broadcast;

      if (!broadcast) throw new Error('Broadcast item cannot be found');

      setBroadcast(broadcast);

      /**
       * `broadcast.actual_start_at` timestamp is needed only for Gen2 because
       * Gen2 does not provide accurate live stream position
       *
       * Note:
       * - IOS - will always start position/duration at 0 when first enter stream
       * - Android - live stream position will cycle within a 15 second range (ex MED-1851)
       */
      if (!isSeekable) {
        /**
         * Test result:
         * if start consuming live stream as soon as the broadcast starts...
         * - Best case - takes 50 seconds
         * - Worst case - takes 90 seconds (maybe worse?)
         *
         * to retrieve `broadcast.actual_start_at` property from the service call
         */
        debug('Broadcast is Gen 2');
        if (broadcast.actual_start_at) {
          debug('Broadcast `actual_start_at` time is found!');
        } else {
          debug(
            'Broadcast `actual_start_at` time is missing. Try again in 10 seconds'
          );
          pollBroadcastId = setTimeout(() => initFetches(), 10 * 1000);

          return;
        }
      }

      pollPlayerPrompts({ mediaItem, appKey: props.appKey || '' });
    } catch (e) {
      console.warn('fetch resource failed', e);
    }
  };

  /** Log Analytics */
  const sendPromptEvent = (event: PromptEvent) => {
    AnalyticsService.sendEvent({
      type: 'play-events',
      appKey: props.appKey || '',
      data: {
        event,
        playId: playId || '',
        position: positionMsec,
        format: 'live_video',
      },
    });
  };

  /** ------------------------- EFFECTS ------------------------- */

  /** Unmount clean up */
  useEffect(() => {
    return () => {
      debug('Clean up buffer grace period timeout');
      clearTimeout(bufferGraceTimeoutId);
    };
  }, []);

  /** Update playback states on mount */
  useEffect(() => {
    if (!props.useNativeMediaPlayer) return;

    getMediaPlaybackStateInfo()
      .then(updatePlaybackStates)
      .catch((e) => {
        debug('Failed to update states', e);
      });
  }, [props.useNativeMediaPlayer, updatePlaybackStates]);

  /** Javascript bridge - register media event listeners */
  useEffect(() => {
    if (!props.useNativeMediaPlayer) return;

    const listeners = _registerEmitters();

    return () => {
      listeners?.forEach((listener) => {
        listener?.remove();
      });
    };
  }, [_registerEmitters, props.useNativeMediaPlayer]);

  /**
   * Non-native media player events
   * pass in media events as props instead of listening for
   * events from the react native bridge
   */
  useEffect(() => {
    if (props.useNativeMediaPlayer) return;

    updatePlaybackStates(props);
  }, [props, updatePlaybackStates]);

  /**  Set polling */
  useEffect(() => {
    if (mediaItemShortCode && isLiveStream) {
      setIsPolling(Boolean(isPlaying));
    }
  }, [mediaItemShortCode, isPlaying, isLiveStream]);

  /** Poll position */
  useEffect(() => {
    if (isPolling && !pollPositionId) {
      updatePosition();
      pollPositionId = setInterval(updatePosition, POLL_POSITION_INTERVAL);
      debug('started polling with interval id:' + pollPositionId);
    } else if (!isPolling && pollPositionId) {
      clearInterval(pollPositionId);
      pollPositionId = null;
      debug('stop polling for interval id:' + pollPositionId);
    }

    return () => {
      debug('cleaned up interval - pollPositionId ');
      if (pollPositionId) {
        clearInterval(pollPositionId);
        pollPositionId = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, pollPositionId]);

  /** Update giving url when mediaItemShortCode is received */
  useEffect(() => {
    let url = `${Environment.newGivingHost}/u/-${props.appKey}/give?utm_campaign=media_giving&utm_source=${Platform.OS}&utm_medium=prompt`;

    if (mediaItemShortCode) {
      url += `&utm_content=${props.appKey}_${mediaItemShortCode}`;
    }

    setGivingUrl(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaItemShortCode]);

  /** Fetches media item and player prompts */
  useEffect(() => {
    if (!mediaItemShortCode || !isLiveStream) return;

    initFetches();

    return () => {
      debug('cleaned up timeout - pollPlayerPromptId and pollBroadcastId');
      clearTimeout(pollPlayerPromptId);
      clearTimeout(pollBroadcastId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaItemShortCode, isLiveStream]);

  /**
   * Issue:
   * There is a tiny gap when isSeeking flag === true and when the
   * isNativeBuffering flag === true. This  would cause inaccurate
   * prompt rendering during scrubbing (MED-1862)
   *
   * Exact state behavior when user scrubs the video:
   * 1. isSeeking === true, isNativeBuffering === false
   * 2. isSeeking === false, isNativeBuffering === false
   * --------- the unwanted gap, seeking should immediately start buffering --------
   * 3. isSeeking === false, isNativeBuffering === true
   * 4. isSeeking === false, isNativeBuffering === false
   *
   * Solution:
   * Add a `isBuffering` state to more accurately portray client's expectation
   * of the video buffering by including a grace period between the gap.
   *
   * Buffer Grace:
   * - Enables when seeking starts
   * - Disables when the Native Buffering transitions from true to false
   *
   * (Solved/Expected) behavior when user scrubs the video:
   * 1. isSeeking === true, isNativeBuffering === false, isBuffering === false
   * 2. (new) isSeeking === true, isNativeBuffering === false, isBuffering === true
   * 3. isSeeking === false, isNativeBuffering === false, isBuffering === true
   * 4. isSeeking === false, isNativeBuffering === true, isBuffering === true
   * 5. isSeeking === false, isNativeBuffering === false, isBuffering === true
   * 6. (new) isSeeking === false, isNativeBuffering === false, isBuffering === false
   *
   * Assumption:
   * 1. Seeking (scrubbing video) will cause video to buffer
   */
  useEffect(() => {
    if (isSeeking) {
      debug('Enable buffering grace period');
      bufferGrace = true;
      setIsBuffering(true);

      clearTimeout(bufferGraceTimeoutId);
      bufferGraceTimeoutId = setTimeout(() => {
        bufferGrace = false;
      }, BUFFER_GRACE_RESET_TIMEOUT);
    }
  }, [isSeeking]);

  /** Update isBuffering state */
  useEffect(() => {
    if (bufferGrace && prevIsNativeBuffering && !isNativeBuffering) {
      debug('Disable buffering grace period');
      bufferGrace = false;
      clearTimeout(bufferGraceTimeoutId);
    }

    if (!bufferGrace) {
      setIsBuffering(isNativeBuffering);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNativeBuffering]);

  /** Show or hide player prompt */
  useEffect(() => {
    if (
      !playerPrompt ||
      !broadcast ||
      !isLiveStream ||
      isSeeking ||
      isBuffering ||
      /**
       * fix player prompt flickering
       * sometimes positionMsec will return 0
       * which causes prompt to not render
       * */
      !positionMsec
    ) {
      return;
    }

    debug(
      'isPlaying = ' + isPlaying,
      'isSeekable (Gen3) = ' + isSeekable,
      'positionMsec = ' + positionMsec,
      'durationMsec = ' + durationMsec
    );

    /** Defaults for Gen 3 */
    let broadcastCurrentPosition = (positionMsec || 0) / 1000;

    /** Identify Gen2 and check if `broadcast.actual_start`_at exists */
    if (!isSeekable) {
      const now = moment.utc();

      if (broadcast.actual_start_at) {
        broadcastCurrentPosition = now.diff(
          moment.utc(broadcast.actual_start_at),
          'seconds',
          true
        );
      } else {
        /**
         * The estimated start time is a placeholder until
         * the `broadcast.actual_start_at` is retrieved from service call
         *
         * This will prevent prompt showing and hiding during position
         * cycling on android devices (MED-1851)
         */
        broadcastCurrentPosition = now.diff(
          moment.utc(broadcastEstimatedStartTime),
          'seconds',
          true
        );
      }
    }

    debug('broadcastCurrentPosition = ' + broadcastCurrentPosition);

    /** Calculate player prompt start/end time relative to the beginning of broadcast */
    const { trigger_time = 0, display_duration_secs = 45 } = playerPrompt;

    if (promptStartTime === null || promptEndTime === null) {
      setPromptStartTime(trigger_time);
      setPromptEndTime(trigger_time + display_duration_secs);
    }

    /** Show giving prompt according to the prompt start/end time */
    if (
      typeof promptStartTime === 'number' &&
      typeof promptEndTime === 'number' &&
      promptStartTime <= broadcastCurrentPosition &&
      broadcastCurrentPosition < promptEndTime
    ) {
      if (!dismissedPrompt) {
        setGivingCardVisible(true);
        sendPromptEvent(PromptEvent.promptShow);
      }
    } else {
      setGivingCardVisible(false);
      setDismissedPrompt(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPlaying,
    isSeekable,
    playerPrompt,
    positionMsec,
    durationMsec,
    broadcast?.actual_start_at,
    isSeeking,
    isBuffering,
  ]);

  /** ------------------------- RENDER ------------------------- */

  if (!windowWidth || !contentMargin || !givingCardVisible) return null;

  return (
    <KitSafeAreaView
      style={{
        flex: 1,
        maxHeight: 200,
      }}
    >
      <KitTouchable
        onPress={
          playerPrompt && !playerPrompt.giving.default_amounts
            ? () => {
                sendPromptEvent(PromptEvent.promptClick);
                openLink(givingUrl);
              }
            : null
        }
        style={{
          alignItems: 'center',
          backgroundColor: Colors.N0,
          borderRadius: 10,
          elevation: 5,
          justifyContent: 'center',
          marginHorizontal: contentMargin - 2,
          maxWidth,
          shadowOffset: { width: 0, height: 10 },
          shadowRadius: 25,
          padding: 18,
          ...props.styles,
        }}
      >
        <View
          style={{
            alignItems: 'center',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <View style={{ alignSelf: 'flex-start', marginTop: Spacing.m }}>
              <KitIcon name='give' size={20} color='#4F63FF' />
            </View>
            <View style={{ flexDirection: 'column', marginLeft: 14 }}>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <KitText subtitle extraBold color={Colors.N1000}>
                  {playerPrompt?.title || 'Join us in giving'}
                </KitText>
                <KitTouchable
                  onPress={() => {
                    setDismissedPrompt(true);
                    setGivingCardVisible(false);
                    sendPromptEvent(PromptEvent.promptDismissed);
                  }}
                >
                  <KitIcon name='remove' size={18} color='#b8babf' />
                </KitTouchable>
              </View>
              <KitText
                fontSize={14}
                color='#747578'
                style={{ display: 'flex', flexWrap: 'wrap' }}
              >
                {playerPrompt?.body || 'Setup a one-time or repeating gift'}
              </KitText>
              <GiveAmounts
                givingUrl={givingUrl}
                showDefaultAmounts={Boolean(
                  playerPrompt?.giving.default_amounts
                )}
                sendPromptEvent={sendPromptEvent}
              />
            </View>
          </View>
        </View>
      </KitTouchable>
    </KitSafeAreaView>
  );
};
